import React, { useEffect, useRef, useState, useCallback, forwardRef, useImperativeHandle } from 'react';
import { Box, Text, useColorModeValue, useToast } from '@chakra-ui/react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { AlignableImage } from './extensions/AlignableImage';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import TextAlign from '@tiptap/extension-text-align';
import Underline from '@tiptap/extension-underline';
import Color from '@tiptap/extension-color';
import { TextStyle } from '@tiptap/extension-text-style';
import Highlight from '@tiptap/extension-highlight';
import { Table } from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import EmailEditorToolbar from './EmailEditorToolbar';
import ImageUploadButton from './ImageUploadButton';
import { firebase } from '../../../utils/init-firebase';
import { useAuth } from '../../../contexts/AuthContext';

const IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'];
const MAX_IMAGE_SIZE = 5 * 1024 * 1024;

const RichTextEmailEditor = forwardRef(function RichTextEmailEditor(
  { value, onChange, placeholder, minHeight = '250px', toolbarExtra, onFileDrop },
  ref
) {
  const isInternalUpdate = useRef(false);
  const [dragOver, setDragOver] = useState(false);
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const editorBg = useColorModeValue('white', 'gray.900');
  const { currentUser } = useAuth();
  const toast = useToast();

  const uploadImageToStorage = useCallback(async (file) => {
    if (!IMAGE_TYPES.includes(file.type)) return null;
    if (file.size > MAX_IMAGE_SIZE) {
      toast({ title: 'Image too large', description: 'Max 5 MB.', status: 'warning', duration: 3000 });
      return null;
    }
    try {
      const uid = currentUser?.uid || 'anonymous';
      const path = `email-images/${uid}/${Date.now()}_${file.name || 'pasted-image'}`;
      const storageRef = firebase.storage().ref(path);
      await storageRef.put(file);
      return await storageRef.getDownloadURL();
    } catch (err) {
      console.error('Image upload failed:', err);
      toast({ title: 'Image upload failed', description: err.message, status: 'error', duration: 3000 });
      return null;
    }
  }, [currentUser, toast]);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
        link: false,
        underline: false,
      }),
      AlignableImage.configure({
        allowBase64: false,
        inline: false,
        // Responsive by default so large images never overflow the recipient's screen.
        HTMLAttributes: { style: 'max-width:100%;height:auto;' },
        // Drag-to-resize handles (aspect-locked). Hold Shift is not required.
        resize: {
          enabled: true,
          directions: ['top-left', 'top-right', 'bottom-left', 'bottom-right'],
          minWidth: 40,
          minHeight: 40,
          alwaysPreserveAspectRatio: true,
        },
      }),
      Link.configure({
        openOnClick: false,
        autolink: true,
        HTMLAttributes: { rel: 'noopener noreferrer', target: '_blank' },
      }),
      Placeholder.configure({
        placeholder: placeholder || 'Compose your email...',
      }),
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Underline,
      Color,
      TextStyle,
      Highlight.configure({ multicolor: true }),
      Table.configure({ resizable: false }),
      TableRow,
      TableCell,
      TableHeader,
    ],
    content: value || '',
    onUpdate: ({ editor: ed }) => {
      isInternalUpdate.current = true;
      const html = ed.getHTML();
      onChange?.(html);
    },
    // Handle pasted images: upload to Storage, insert as hosted URL
    editorProps: {
      handlePaste: (view, event) => {
        const items = event.clipboardData?.items;
        if (!items) return false;

        for (const item of items) {
          if (item.type.startsWith('image/')) {
            event.preventDefault();
            const file = item.getAsFile();
            if (!file) return true;
            uploadImageToStorage(file).then((url) => {
              if (url && view.state.tr) {
                view.dispatch(
                  view.state.tr.replaceSelectionWith(
                    view.state.schema.nodes.image.create({ src: url })
                  )
                );
              }
            });
            return true;
          }
        }
        return false;
      },
    },
  });

  useImperativeHandle(ref, () => editor, [editor]);

  useEffect(() => {
    if (!editor) return;
    if (isInternalUpdate.current) {
      isInternalUpdate.current = false;
      return;
    }
    const currentHtml = editor.getHTML();
    const incoming = value || '';
    if (incoming !== currentHtml) {
      editor.commands.setContent(incoming, false);
    }
  }, [value, editor]);

  // Drag-and-drop handler: images go into the editor body, other files go to onFileDrop (attachments)
  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  }, []);

  const handleDrop = useCallback(async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);

    const files = Array.from(e.dataTransfer?.files || []);
    if (files.length === 0) return;

    for (const file of files) {
      if (IMAGE_TYPES.includes(file.type)) {
        const url = await uploadImageToStorage(file);
        if (url && editor) {
          editor.chain().focus().setImage({ src: url }).run();
        }
      } else if (onFileDrop) {
        onFileDrop(file);
      }
    }
  }, [editor, uploadImageToStorage, onFileDrop]);

  return (
    <Box>
      <EmailEditorToolbar
        editor={editor}
        extraButtons={
          <>
            <ImageUploadButton editor={editor} />
            {toolbarExtra}
          </>
        }
      />
      <Box
        position="relative"
        borderWidth="1px"
        borderTopWidth="0"
        borderColor={dragOver ? 'blue.400' : borderColor}
        borderBottomRadius="md"
        bg={editorBg}
        minH={minHeight}
        transition="border-color 0.15s"
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        sx={{
          '.ProseMirror': {
            minH: minHeight,
            px: 3,
            py: 2,
            outline: 'none',
            fontSize: 'sm',
            lineHeight: '1.6',
            'p': { minHeight: '1.6em', margin: 0 },
            'p.is-editor-empty:first-of-type::before': {
              content: 'attr(data-placeholder)',
              float: 'left',
              color: 'gray.400',
              pointerEvents: 'none',
              height: 0,
            },
            'h1': { fontSize: '2xl', fontWeight: 'bold', mt: 4, mb: 2 },
            'h2': { fontSize: 'xl', fontWeight: 'bold', mt: 3, mb: 2 },
            'h3': { fontSize: 'lg', fontWeight: 'semibold', mt: 2, mb: 1 },
            'ul, ol': { pl: 6, my: 2 },
            'ul': { listStyleType: 'disc' },
            'ol': { listStyleType: 'decimal' },
            'li': { my: 1 },
            'li::marker': { fontSize: 'inherit', color: 'inherit' },
            'blockquote': {
              borderLeft: '3px solid',
              borderColor: 'gray.300',
              pl: 3,
              ml: 0,
              my: 2,
              color: 'gray.600',
            },
            'a': { color: 'blue.500', textDecoration: 'underline' },
            'img': { maxWidth: '100%', h: 'auto', my: 2, borderRadius: 'md' },
            // Bare (non-resizable) selected image fallback
            'img.ProseMirror-selectednode': {
              outline: '2px solid',
              outlineColor: 'blue.400',
            },
            // Resizable image node view: a flex container (handles alignment via
            // justify-content) wrapping the image; handles are absolutely placed.
            '[data-resize-container]': { maxWidth: '100%' },
            '[data-resize-wrapper] img': { my: 0 },
            '[data-resize-container].ProseMirror-selectednode [data-resize-wrapper]': {
              outline: '2px solid',
              outlineColor: 'blue.400',
            },
            '[data-resize-handle]': {
              width: '12px',
              height: '12px',
              bg: 'blue.500',
              border: '2px solid',
              borderColor: 'white',
              borderRadius: '2px',
              boxShadow: '0 0 0 1px rgba(0,0,0,0.25)',
              opacity: 0,
              transition: 'opacity 0.12s ease',
              zIndex: 20,
            },
            '[data-resize-container]:hover [data-resize-handle], [data-resize-container].ProseMirror-selectednode [data-resize-handle]': {
              opacity: 1,
            },
            '[data-resize-handle="top-left"]': { cursor: 'nwse-resize' },
            '[data-resize-handle="top-right"]': { cursor: 'nesw-resize' },
            '[data-resize-handle="bottom-left"]': { cursor: 'nesw-resize' },
            '[data-resize-handle="bottom-right"]': { cursor: 'nwse-resize' },
            'hr': { my: 4, borderColor: 'gray.300' },
            'table': {
              borderCollapse: 'collapse',
              w: '100%',
              my: 2,
              'th, td': {
                border: '1px solid',
                borderColor: 'gray.300',
                px: 2,
                py: 1,
                fontSize: 'sm',
              },
              'th': { bg: 'gray.50', fontWeight: 'semibold' },
            },
          },
        }}
      >
        <EditorContent editor={editor} />
        {dragOver && (
          <Box
            position="absolute"
            inset={0}
            bg="blue.50"
            opacity={0.7}
            borderRadius="md"
            display="flex"
            alignItems="center"
            justifyContent="center"
            pointerEvents="none"
            zIndex={10}
          >
            <Text fontSize="md" fontWeight="bold" color="blue.600">
              Drop file here
            </Text>
          </Box>
        )}
      </Box>
    </Box>
  );
});

export default RichTextEmailEditor;
